import { Copy, TrashCan, Upload } from '@carbon/icons-react';
import { Button, InlineNotification, Modal, Tag } from '@carbon/react';
import {
  asProblem,
  ChoiceDropdown,
  EmptyState,
  FilterLayout,
  ListTable,
  PageHeader,
  problemMessage,
  RowAction,
  useAdmin,
  useFilters,
  useFormat,
  useReferenceOptions,
} from '@pos/admin-ui';
import { ConfirmDialog } from '@pos/design-system';
import { useTranslation } from '@pos/i18n';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorNotice, TransientNotice } from '../common/Notices';
import { ImageUploader } from './ImageUploader';
import { canManageImages, canReadImages, deleteImage, listImages, imagesKey, type ImageAsset } from './imagesApi';
import { useImageText } from './imageText';
import './images.scss';

/**
 * CMS › Images (FR-ONL-17, ADR 0066): the images uploaded at HO, newest first, as every admin list (DESIGN_SYSTEM.md §13): search
 * by file name or description and *Upload images* in the table toolbar, a brand filter, pages of 25/50/100. *Upload images* opens
 * Carbon's file uploader in a dialog. Each row shows the image, its name with its description (or that it has none: the website
 * needs it for people who can't see the image), its size and file size, brand and upload time; copy its address into a logo, item
 * or website field, or delete it from the list (its file stays, so pages that show it keep working).
 */
export function ImagesPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const { session } = useAdmin();
  const canRead = canReadImages(session);
  const canManage = canManageImages(session);
  const text = useImageText();
  const queryClient = useQueryClient();
  const brands = useReferenceOptions('org.brand');
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const filters = useFilters({ brand: '' }, () => setPaging((p) => ({ ...p, page: 1 })));
  const list = useQuery({
    queryKey: [...imagesKey, filters.applied, search, paging],
    queryFn: () => listImages({ brandId: filters.applied.brand, search, ...paging }),
    placeholderData: keepPreviousData,
    enabled: canRead,
  });
  const [notice, setNotice] = useState<string>();
  const [copyError, setCopyError] = useState<string>();
  const [deleting, setDeleting] = useState<ImageAsset>();
  const remove = useMutation({
    mutationFn: (image: ImageAsset) => deleteImage(image.id),
    onSuccess: async () => {
      setDeleting(undefined);
      setNotice(t('images.deleted'));
      await queryClient.invalidateQueries({ queryKey: imagesKey });
    },
  });

  const brandName = (id: string | null) =>
    id ? (brands.data?.find((b) => b.value === id)?.label ?? '') : t('images.allBrands');

  const copy = async (image: ImageAsset) => {
    setNotice(undefined);
    setCopyError(undefined);
    try {
      await navigator.clipboard.writeText(image.url);
      setNotice(t('images.copied', { name: text.name(image) }));
    } catch {
      setCopyError(t('images.copyFailed', { url: image.url }));
    }
  };

  if (!canRead) {
    return (
      <section className="pos-admin-page">
        <EmptyState heading={t('admin.errors.forbiddenTitle')} description={t('admin.errors.forbidden')} />
      </section>
    );
  }

  return (
    <section className="pos-admin-page pos-images">
      <PageHeader title={t('images.title')} description={t('images.intro')} />
      {notice && <TransientNotice kind="success" title={notice} onDone={() => setNotice(undefined)} />}
      {copyError && <ErrorNotice title={copyError} />}
      <FilterLayout
        filters={filters}
        fields={
          <ChoiceDropdown
            id="images-brand"
            label={t('images.columns.brand')}
            choices={(brands.data ?? []).map((b) => ({ id: b.value, label: b.label }))}
            value={filters.draft.brand ?? ''}
            onChange={(v) => filters.set('brand', v)}
          />
        }
      >
        <ListTable<ImageAsset>
          ariaLabel={t('images.title')}
          loading={list.isLoading}
          error={list.error ? problemMessage(t, asProblem(list.error)) : undefined}
          onRetry={() => void list.refetch()}
          rows={list.data?.items ?? []}
          filters={filters}
          search={{
            label: t('images.search'),
            onChange: (value) => {
              setSearch(value.trim());
              setPaging((p) => ({ ...p, page: 1 }));
            },
          }}
          toolbarActions={
            canManage && (
              <Button kind="primary" renderIcon={Upload} onClick={() => setUploading(true)}>
                {t('images.upload.title')}
              </Button>
            )
          }
          columns={[
            {
              key: 'image',
              header: t('images.columns.image'),
              render: (p) => <img className="pos-images__thumb" src={p.url} alt={p.altText ?? ''} loading="lazy" />,
            },
            {
              key: 'name',
              header: t('images.columns.name'),
              render: (p) => (
                <div className="pos-images__cell">
                  <span>{text.name(p)}</span>
                  {p.altText ? (
                    <span className="pos-images__secondary">{p.altText}</span>
                  ) : (
                    <span>
                      <Tag type="warm-gray">{t('images.noDescription')}</Tag>
                    </span>
                  )}
                </div>
              ),
            },
            {
              key: 'size',
              header: t('images.columns.size'),
              render: (p) => (
                <div className="pos-images__cell">
                  <span>{text.dimensions(p)}</span>
                  <span className="pos-images__secondary">{text.fileSize(p)}</span>
                </div>
              ),
            },
            { key: 'brand', header: t('images.columns.brand'), render: (p) => brandName(p.brandId) },
            { key: 'createdAt', header: t('images.columns.uploaded'), render: (p) => format.dateTime(p.createdAt) },
          ]}
          rowActions={(p) => (
            <>
              <RowAction action={t('images.copy')} record={text.name(p)} icon={Copy} onClick={() => void copy(p)} />
              {canManage && (
                <RowAction
                  action={t('common.delete')}
                  record={text.name(p)}
                  icon={TrashCan}
                  onClick={() => {
                    remove.reset();
                    setDeleting(p);
                  }}
                />
              )}
            </>
          )}
          empty={
            filters.count > 0 || search
              ? undefined
              : { heading: t('admin.emptyTitle'), description: t(canManage ? 'images.empty' : 'images.emptyReadOnly') }
          }
          pagination={{
            ...paging,
            total: Number(list.data?.total ?? 0),
            onChange: ({ page, pageSize }) => setPaging({ page, pageSize }),
          }}
        />
      </FilterLayout>
      {uploading && (
        <Modal
          open
          passiveModal
          size="md"
          modalHeading={t('images.upload.title')}
          onRequestClose={() => setUploading(false)}
        >
          <ImageUploader heading={false} />
        </Modal>
      )}
      {deleting && (
        <ConfirmDialog
          open
          title={t('images.deleteTitle')}
          confirmLabel={t('common.delete')}
          busy={remove.isPending}
          onConfirm={() => remove.mutate(deleting)}
          onCancel={() => setDeleting(undefined)}
        >
          <p>{t('images.deleteBody', { name: text.name(deleting) })}</p>
          {remove.error && (
            <InlineNotification
              kind="error"
              role="alert"
              lowContrast
              hideCloseButton
              title={problemMessage(t, asProblem(remove.error))}
            />
          )}
        </ConfirmDialog>
      )}
    </section>
  );
}
